import { createApi } from '@reduxjs/toolkit/query/react';

import { setPages, setSections } from 'store/wm/wmSlice';

import { logDataDogEvent } from 'utils/datadog';
import { baseQueryWithReauth } from 'utils/services';

import {
  AddReminderReqProps,
  CreateRenewalReqProps,
  CreateRenewalResProps,
  GetArticleByIdReqProps,
  GetArticleByIdResProps,
  GetOrdersReqProps,
  GetOrdersResProps,
  GetPagesReqProps,
  GetPagesResProps,
  GetSurveyDetailsReqProps,
  GetSurveyDetailsResProps,
  GetSurveyReqProps,
  GetSurveyResProps,
  MarkPageViewedReqProps,
  MarkPageViewedResProps,
  NewDeviceMeasurementsReqProps,
  NewDeviceMeasurementsResProps,
  SelectRenewalOrderReqProps,
  SubmitSurveyReqProps,
  SubmitSurveyResProps,
  SurveyResponsesReqProps,
  SurveyResponsesResProps,
  UpdateSurveyReqProps,
  UpdateSurveyResProps
} from './wm.types';

export const wmApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addReminder: build.mutation<unknown, AddReminderReqProps>({
      query: ({ surveyId, ...body }) => ({
        body,
        method: 'POST',
        url: `/wm/surveys/${surveyId}/reminder`
      })
    }),
    checkLabsTask: build.mutation<unknown, { surveyId: string }>({
      query: ({ surveyId }) => ({
        body: { surveyId },
        method: 'POST',
        url: '/tasks/create/lab-order-request'
      })
    }),
    createRenewal: build.mutation<CreateRenewalResProps, CreateRenewalReqProps>({
      query: ({ orderId }) => ({
        method: 'POST',
        url: `/wm/orders/${orderId}/surveys/renewal`
      })
    }),
    dismissRenewalLifemdConsent: build.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: '/wm/renewal/lifemd-consent-dismiss'
      })
    }),
    getArticleById: build.query<GetArticleByIdResProps, GetArticleByIdReqProps>({
      keepUnusedDataFor: 0,
      query: ({ articleId }) => ({
        url: `/articles/${articleId}`
      })
    }),
    getPages: build.query<GetPagesResProps, GetPagesReqProps>({
      keepUnusedDataFor: 0,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              setPages(data.data.map((page) => ({ ...page, section: page.section?.toLowerCase() })))
            );

            const pageSectionIdSet = new Set(data.data.map((page) => page.section?.toLowerCase()));
            const sections = data.info?.sections.filter((section) => {
              // Page that links directly to this section
              const isLinkedByPage = pageSectionIdSet.has(section._id.toLowerCase());
              // At least one child section has a page
              const childHasPage = data.info?.sections.some((child) => {
                const isChildOfSection = child.parentIds?.some(
                  (id) => id.toLowerCase() === section._id.toLowerCase()
                );
                const childLinkedByPage = pageSectionIdSet.has(child._id.toLowerCase());
                return isChildOfSection && childLinkedByPage;
              });

              return isLinkedByPage || childHasPage;
            });

            dispatch(setSections(sections));
          }
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'getPages failed'
          });
        }
      },
      query: ({ programId }) => ({
        url: `/programs/${programId}/articles`
      })
    }),
    getSurvey: build.query<GetSurveyResProps, GetSurveyReqProps>({
      query: ({ orderId, type }) => ({
        url: `/wm/surveys/${type}/${orderId}`
      })
    }),
    getSurveyDetails: build.query<GetSurveyDetailsResProps, GetSurveyDetailsReqProps>({
      keepUnusedDataFor: 0,
      query: ({ orderId, type }) => ({ url: `/wm/survey-details/${type}/${orderId}` })
    }),
    getWMOrders: build.query<GetOrdersResProps, GetOrdersReqProps>({
      query: (params) => ({
        params,
        url: '/wm/orders'
      })
    }),
    markPageViewed: build.mutation<MarkPageViewedResProps, MarkPageViewedReqProps>({
      query: ({ wmPageId, ...body }) => ({
        body,
        method: 'PUT',
        url: `wm-pages/${wmPageId}`
      })
    }),
    newDeviceMeasurements: build.mutation<
      NewDeviceMeasurementsResProps,
      NewDeviceMeasurementsReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/wm/new-device-measurements'
      })
    }),
    selectRenewalOrder: build.mutation<unknown, SelectRenewalOrderReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/wm/select-renewal-order'
      })
    }),
    submitSurvey: build.mutation<SubmitSurveyResProps, SubmitSurveyReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/wm/submit-survey'
      })
    }),
    surveyResponses: build.mutation<SurveyResponsesResProps, SurveyResponsesReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/wm/survey-responses'
      })
    }),
    updateSurvey: build.mutation<UpdateSurveyResProps, UpdateSurveyReqProps>({
      query: ({ orderId, type, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/wm/surveys/${type}/${orderId}`
      })
    })
  }),
  reducerPath: 'wmApi',
  tagTypes: ['WeightManagement']
});

export const {
  useAddReminderMutation,
  useCheckLabsTaskMutation,
  useCreateRenewalMutation,
  useDismissRenewalLifemdConsentMutation,
  useGetArticleByIdQuery,
  useGetPagesQuery,
  useGetSurveyDetailsQuery,
  useGetSurveyQuery,
  useGetWMOrdersQuery,
  useLazyGetArticleByIdQuery,
  useMarkPageViewedMutation,
  useNewDeviceMeasurementsMutation,
  useSelectRenewalOrderMutation,
  useSubmitSurveyMutation,
  useSurveyResponsesMutation,
  useUpdateSurveyMutation
} = wmApi;
